export type StreamingEvent = {
  event: string;
  data: string;
};

export interface IStreamingCompletionResponse {
  onEvent(callback: (event: StreamingEvent) => void): Promise<void>;
}

export class StreamingCompletionResponse implements IStreamingCompletionResponse {
  private streamingFetchResponse: Response;

  constructor(streamingFetchResponse: Response) {
    this.streamingFetchResponse = streamingFetchResponse;
  }

  async onEvent(callback: (event: StreamingEvent) => void): Promise<void> {
    if (!this.streamingFetchResponse.body) {
      throw new Error('Response body is not available for streaming.');
    }

    const reader = this.streamingFetchResponse.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let currentEvent = 'message';
    let dataLines: string[] = [];

    const emitEvent = () => {
      if (dataLines.length === 0) {
        currentEvent = 'message';
        return;
      }
      callback({ event: currentEvent, data: dataLines.join('\n') });
      currentEvent = 'message';
      dataLines = [];
    };

    const ingestLine = (rawLine: string) => {
      const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
      if (line === '') {
        emitEvent();
        return;
      }
      if (line.startsWith(':')) {
        return;
      }
      if (line.startsWith('event:')) {
        const nextEvent = line.slice(6).trim();
        currentEvent = nextEvent || 'message';
        return;
      }
      if (line.startsWith('data:')) {
        let value = line.slice(5);
        if (value.startsWith(' ')) {
          value = value.slice(1);
        }
        dataLines.push(value);
      }
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        let newlineIndex = buffer.indexOf('\n');
        while (newlineIndex >= 0) {
          const line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);
          ingestLine(line);
          newlineIndex = buffer.indexOf('\n');
        }
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      console.error('Error while reading stream:', error);
      throw error;
    } finally {
      reader.releaseLock();
    }

    if (buffer.length > 0) {
      ingestLine(buffer);
    }
    emitEvent();
  }
}

export class SimulatedStreamingResponse implements IStreamingCompletionResponse {
  private content: string;
  private chunkSize: number;
  private delayMs: number;

  constructor(content: string, chunkSize: number = 5, delayMs: number = 30) {
    this.content = content;
    this.chunkSize = chunkSize;
    this.delayMs = delayMs;
  }

  async onEvent(callback: (event: StreamingEvent) => void): Promise<void> {
    let currentIndex = 0;
    const streamChunks = async () => {
      while (currentIndex < this.content.length) {
        const chunk = this.content.substring(currentIndex, Math.min(currentIndex + this.chunkSize, this.content.length));
        callback({ event: 'message', data: chunk });
        currentIndex += this.chunkSize;
        if (currentIndex < this.content.length) {
          await new Promise(resolve => setTimeout(resolve, this.delayMs));
        }
      }
    };

    await streamChunks();
  }
}
